'use client';

import { useMemo, useRef, useState } from 'react';
import { atbash, caesar, caesarCandidates, rot13, vigenere } from '@/lib/browser-ciphers';
import { consumeToolUsage } from '@/lib/browser-tool-usage';

type Variant = 'caesar' | 'vigenere' | 'atbash' | 'rot13';

const SAMPLE: Record<Variant, string> = {
  caesar: 'Wkh txlfn eurzq ira mxpsv ryhu wkh odcb grj.',
  vigenere: 'Lxfopv ef rnhr!',
  atbash: 'Gsv jfrxp yildm ulc qfnkh levi gsv ozab wlt.',
  rot13: 'Gur dhvpx oebja sbk whzcf bire gur ynml qbt.',
};

export default function BrowserCipher({
  config,
}: {
  config?: { toolSlug?: string; heading?: string; variant?: string };
}) {
  const variant = (config?.variant ?? 'caesar') as Variant;
  const [text, setText] = useState(SAMPLE[variant]);
  const [shift, setShift] = useState(3);
  const [key, setKey] = useState('LEMON');
  const [decode, setDecode] = useState(true);
  const [copied, setCopied] = useState(false);
  const reported = useRef(false);
  const report = () => {
    if (reported.current) return;
    reported.current = true;
    void consumeToolUsage(config?.toolSlug ?? `${variant}-cipher`, '').catch(() => undefined);
  };

  const output = useMemo(() => {
    if (variant === 'atbash') return atbash(text);
    if (variant === 'rot13') return rot13(text);
    if (variant === 'vigenere') return vigenere(text, key, decode);
    return caesar(text, decode ? -shift : shift);
  }, [variant, text, shift, key, decode]);

  const candidates = useMemo(
    () => (variant === 'caesar' && decode ? caesarCandidates(text).slice(0, 5) : []),
    [variant, decode, text],
  );
  const symmetric = variant === 'atbash' || variant === 'rot13';

  return (
    <div className="tool-live-shell">
      <div className="tool-live-header">
        <span className="tool-live-badge">Cipher</span>
        <h2 className="tool-live-title">{config?.heading ?? 'Cipher'}</h2>
        <p className="tool-live-subtitle">
          {symmetric
            ? 'The same operation encodes and decodes. Case, spaces and punctuation are kept.'
            : 'Encode or decode as you type. Case, spaces and punctuation are kept.'}
        </p>
      </div>
      <div className="tool-live-grid">
        <section className="tool-live-uploader">
          <label className="tool-live-field tool-live-field-full">
            <span>Text</span>
            <textarea
              onChange={(e) => {
                report();
                setText(e.target.value);
              }}
              rows={5}
              value={text}
            />
          </label>
          {!symmetric ? (
            <div className="tool-live-actions" role="group" aria-label="Direction">
              {[true, false].map((d) => (
                <button
                  aria-pressed={decode === d}
                  className={decode === d ? 'button' : 'button button-secondary'}
                  key={String(d)}
                  onClick={() => {
                    report();
                    setDecode(d);
                  }}
                  type="button"
                >
                  {d ? 'Decode' : 'Encode'}
                </button>
              ))}
            </div>
          ) : null}
          {variant === 'caesar' ? (
            <label className="tool-live-field tool-live-field-full">
              <span>Shift: {shift}</span>
              <input
                max={25}
                min={1}
                onChange={(e) => {
                  report();
                  setShift(Number(e.target.value));
                }}
                type="range"
                value={shift}
              />
            </label>
          ) : null}
          {variant === 'vigenere' ? (
            <label className="tool-live-field tool-live-field-full">
              <span>Key (letters only)</span>
              <input
                onChange={(e) => {
                  report();
                  setKey(e.target.value);
                }}
                spellCheck={false}
                type="text"
                value={key}
              />
            </label>
          ) : null}
        </section>
        <section className="tool-live-preview">
          <label className="tool-live-field tool-live-field-full">
            <span>{symmetric ? 'Result' : decode ? 'Decoded text' : 'Encoded text'}</span>
            <textarea readOnly rows={5} value={output} />
          </label>
          <div className="tool-live-actions">
            <button
              className="button button-secondary"
              onClick={() => {
                report();
                void navigator.clipboard?.writeText(output).then(() => {
                  setCopied(true);
                  window.setTimeout(() => setCopied(false), 1500);
                });
              }}
              type="button"
            >
              {copied ? 'Copied' : 'Copy result'}
            </button>
          </div>
          {candidates.length ? (
            <div className="tool-live-panel-note">
              <strong>Don&apos;t know the shift? Most likely:</strong>
              <ul style={{ margin: '0.4rem 0 0', paddingLeft: '1.1rem' }}>
                {candidates.map((c) => (
                  <li key={c.shift}>
                    <button
                      className="soft-link"
                      onClick={() => {
                        report();
                        setShift(c.shift);
                      }}
                      style={{
                        background: 'none',
                        border: 0,
                        padding: 0,
                        cursor: 'pointer',
                        font: 'inherit',
                      }}
                      type="button"
                    >
                      Shift {c.shift}
                    </button>
                    : {c.text.slice(0, 60)}
                    {c.text.length > 60 ? '…' : ''}
                  </li>
                ))}
              </ul>
            </div>
          ) : null}
        </section>
      </div>
    </div>
  );
}
